import { useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { FileReviewPanelPlacement } from '@jarvis/platform-file-review';
import { fileAccessRestrictedHref } from '../storageAccess/fileAccessRestrictedUrls';
import { displayNameFromPath } from '../mediaViewer/mediaFileTypes';
import { MediaViewerTitle } from '../mediaViewer/MediaViewerTitle';
import '../mediaViewer/MediaViewerTitle.css';
import { PdfViewerSurface } from './PdfViewerSurface';
import './PdfViewerPage.css';

export interface PdfViewerPageProps {
  filesLinkTo?: string;
  filesLinkLabel?: string;
  /** Dock discussion panel on the left or right (default right). */
  discussionPlacement?: FileReviewPanelPlacement;
}

export function PdfViewerPage({
  filesLinkTo = '/files',
  filesLinkLabel = 'File Browser',
  discussionPlacement: discussionPlacementProp
}: PdfViewerPageProps) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mountId = (params.get('mountId') ?? '').trim();
  const path = (params.get('path') ?? '').trim();
  const discussionAnchorParam = (params.get('discussionAnchor') ?? '').trim();
  const hasSource = Boolean(mountId && path);

  const title = path ? displayNameFromPath(path) : '';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'JARVIS PDF Viewer';
    return () => {
      document.title = previousTitle;
    };
  }, []);
  const onRestrictedAccess = useCallback(() => {
    navigate(fileAccessRestrictedHref(), { replace: true });
  }, [navigate]);

  if (!hasSource) {
    return (
      <div className="media-viewer-page media-viewer-page--empty">
        <p className="page-sub">No PDF selected.</p>
        <p className="page-sub">
          Open a file from <Link to={filesLinkTo}>{filesLinkLabel}</Link> — use <strong>Preview</strong> or{' '}
          <strong>Open in PDF Viewer</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="ops-fill-page ops-fill-page--bare pdf-viewer-page pdf-viewer-page--active">
      <PdfViewerSurface
        mountId={mountId}
        path={path}
        discussionPlacement={discussionPlacementProp}
        discussionAnchorId={discussionAnchorParam || null}
        onRestrictedAccess={onRestrictedAccess}
        renderHeader={({ placement, discussionExpanded, onDiscussionToggle }) => (
          <div className="media-viewer-page__header">
            <MediaViewerTitle
              kind="pdf"
              title={title}
              path={path}
              placement={placement}
              discussionExpanded={discussionExpanded}
              onDiscussionToggle={onDiscussionToggle}
            />
          </div>
        )}
        className="pdf-viewer-page__surface"
      />
    </div>
  );
}
